import { useState, useEffect } from 'react'
import { useWindowSize } from 'react-use'
import { useDispatch } from 'react-redux'
import { Modal } from 'antd'
import {
    getHolesAndTapsCount,
    useShouldShowThreadTag,
} from '@Orders/OrderDetailsPage/Status/logic'
import orderedItemsStatusQuery from '@Orders/OrderDetailsPage/Queries/orderedItemsStatusQuery'
import { THREADS_KEY } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'

import BuilderModelViewer from '~/apps/consumer/components/BuilderModelViewer'
import Box from '~/core/Box'
import { setViewerInteractionMode } from '~/redux-modules/qmr-viewer/actions'

import PartDetails from './components/PartDetails'
import {
    docPhotosOnly,
    DFM_FEEDBACK_KEY,
    INSPECTION_KEY,
    TRACK_SHIPMENT_KEY,
    useSelectedDoc,
    usePhotosAndAdditionalDocs,
    useThreeDviewerMode,
    SUMMARY_KEY,
} from './logic'
import PartDetailsCard from './components/PartDetails/components/PartAttributes/components/PartDetailsCard'
import InspectionReport from './components/PartDetails/components/InspectionReport'
import ManufacturingFeedback from './components/PartDetails/components/SubPages/ManufacturingFeedback'
import Threads from './components/PartDetails/components/SubPages/Threads'
import { DfmReportsWrapper } from './components/PartDetails/components/SubPages/DfmReports'
import Shipments from './components/PartDetails/components/SubPages/Shipments'
import PartLayoutContainer from './components/InspectionViewer/components/PartLayoutContainer'
import InspectionViewer from './components/InspectionViewer'
import { PartViewerModalType } from './types'
import styles from './styles.css'
import { PartViewerModalHeader } from './components/PartViewerModalHeader'

export default function PartViewerModal(props: PartViewerModalType) {
    const {
        partViewData: {
            isPartViewerModalVisible,
            quantity,
            boundingBox,
            inspectionDocuments,
            inspectionTypes,
            processName,
            materialName,
            twoDdrawing,
            processes,
            revision,
            holes,
            dfmFeedback,
            material,
            reorderFooterData,
            shipment,
            dfmReports,
            qmrId,
            quote,
            deliveredQuantity,
        },
        onClose,
        activePage,
        setActivePage,
        isInspectionReportEnabled,
        placement,
        isImOrder,
        isSalesOrderLine,
        orderId,
        orderName,
        refetchVariables,
        shipmentDetail,
        setSelectedTab,
        isHighTouch = false,
    } = props

    const { tapsCount, holesCount } = getHolesAndTapsCount(holes)

    const shouldShowThreadTag = useShouldShowThreadTag(processName)

    const bodyStyle = {
        height: useWindowSize().height - 80,
        minHeight: 600,
        minWidth: 768,
        padding: 0,
        overflow: 'auto',
        borderRadius: 4,
    }

    const dfmReportsLength = dfmReports?.filter(
        report => report.isPublished,
    )?.length

    const width = useWindowSize().width - 200

    const shouldTrackShipmentPage = Boolean(
        activePage === TRACK_SHIPMENT_KEY && shipmentDetail?.length,
    )

    const [inspectionDocumentIdx, setInspectionDocumentIdx] = useState(0)
    const inspectionDocumentsRef = isSalesOrderLine
        ? (shipment?.inspectionDocuments as any[])
        : inspectionDocuments
    const inspectionDocumentsLength = inspectionDocumentsRef?.length

    const inspectionTypesRef = isSalesOrderLine
        ? shipment?.inspectionTypes
        : inspectionTypes

    const {
        images,
        files,
        fictiv5s,
        certificates,
        manufacturingDrawings: [manufacturingDrawing],
    } = usePhotosAndAdditionalDocs(inspectionDocumentsRef)

    const fictiv5Photos = fictiv5s.filter(docPhotosOnly)
    const certificatePhotos = certificates.filter(docPhotosOnly)
    const fictiv5sAndPhotos = [
        ...fictiv5Photos,
        ...images,
        ...certificatePhotos,
    ]
    const totalImagesLength = fictiv5sAndPhotos.length

    const selectedDoc = useSelectedDoc(
        inspectionDocumentIdx,
        fictiv5sAndPhotos,
        fictiv5s,
        images,
        files,
    )

    const dispatch = useDispatch()

    const mode = useThreeDviewerMode(activePage)

    useEffect(() => {
        dispatch(
            setViewerInteractionMode({
                mode: mode,
                annotatable: false,
                selectAllDfmByDefault: false,
            }),
        )
    }, [dispatch, mode])

    const refetchQuery = [
        {
            query: orderedItemsStatusQuery,
            variables: {
                orderId: orderId,
                statusNotIn: [],
                ...refetchVariables,
            },
            fetchPolicy: 'network-only',
        },
    ]

    return (
        <Modal
            centered
            closable={false}
            maskClosable
            visible={isPartViewerModalVisible}
            onCancel={onClose}
            width={width}
            bodyStyle={bodyStyle}
            className={styles.modalWrapper}
            style={{
                padding: 0,
            }}
            footer={null}
        >
            <Box direction="col" height="100%">
                <PartViewerModalHeader
                    quote={quote}
                    revision={revision}
                    onClose={onClose}
                    activePage={activePage}
                    setActivePage={setActivePage}
                    inspectionDocumentsLength={inspectionDocumentsLength || 0}
                    dfmReportsLength={dfmReportsLength || 0}
                    isImOrder={isImOrder}
                    dfmFeedback={dfmFeedback}
                    tapsCount={tapsCount}
                    deliveredQuantity={deliveredQuantity}
                    showTrackOrderTab={Boolean(shipmentDetail?.length !== 0)}
                    isHighTouch={isHighTouch}
                />
                <Box flexGrow="1" height="calc(100% - 94px)">
                    <PartLayoutContainer>
                        {activePage === INSPECTION_KEY ? (
                            <InspectionViewer
                                placement={placement}
                                selectedDocdownloadUrl={
                                    selectedDoc?.downloadUrl
                                }
                                selectedInspectionDocument={
                                    inspectionDocumentIdx
                                }
                                fictiv5sAndPhotosLength={
                                    fictiv5sAndPhotos.length
                                }
                                fileName={selectedDoc?.fileName}
                                createdAt={selectedDoc?.createdAt}
                                totalImagesLength={totalImagesLength}
                                setInspectionDocumentIdx={
                                    setInspectionDocumentIdx
                                }
                            />
                        ) : (
                            <Box className={styles.viewerWrapper}>
                                <BuilderModelViewer
                                    defaultMode={mode}
                                    partRevision={revision}
                                    dfmFeedback={dfmFeedback || []}
                                    holes={holes || []}
                                />
                            </Box>
                        )}
                    </PartLayoutContainer>
                    <PartDetailsCard
                        title={activePage}
                        onClickBack={setActivePage}
                        onClose={onClose}
                        placement={placement}
                        reorderFooterData={reorderFooterData}
                        orderName={orderName}
                        quote={quote}
                        qmrId={qmrId}
                    >
                        {activePage === SUMMARY_KEY && (
                            <PartDetails
                                tapsCount={tapsCount}
                                holesCount={holesCount}
                                shouldShowThreadTag={shouldShowThreadTag}
                                dfmFeedback={dfmFeedback}
                                placement={placement}
                                setActivePage={setActivePage}
                                activePage={activePage}
                                isInspectionReportEnabled={
                                    isInspectionReportEnabled
                                }
                                quantity={quantity}
                                inspectionDocumentsLength={
                                    inspectionDocumentsLength || 0
                                }
                                processName={processName}
                                materialName={materialName}
                                twoDdrawing={twoDdrawing}
                                processes={processes}
                                material={material}
                                isImOrder={isImOrder}
                                dfmReportsLength={dfmReportsLength || 0}
                                isSalesOrderLine={isSalesOrderLine}
                                orderName={orderName}
                                qmrId={qmrId}
                                quoteOwnerId={quote?.user?.id}
                                shipmentDetails={shipmentDetail}
                                onClose={onClose}
                                setSelectedTab={setSelectedTab}
                            />
                        )}
                        {activePage === INSPECTION_KEY && (
                            <InspectionReport
                                inspectionTypes={inspectionTypesRef}
                                quantity={quantity}
                                boundingBox={boundingBox}
                                processName={processName}
                                materialName={materialName}
                                twoDdrawing={twoDdrawing}
                                processes={processes}
                                fictiv5s={fictiv5s}
                                manufacturingDrawing={manufacturingDrawing}
                                selectedInspectionDocument={
                                    inspectionDocumentIdx
                                }
                                setInspectionDocumentIdx={
                                    setInspectionDocumentIdx
                                }
                                tapsCount={tapsCount}
                                holesCount={holesCount}
                                photos={images}
                                additionalDocs={files}
                                placement={placement}
                                setActivePage={setActivePage}
                                shouldShowThreadTag={shouldShowThreadTag}
                                certificates={certificates}
                                isImOrder={isImOrder}
                            />
                        )}
                        {activePage === THREADS_KEY && holes && (
                            <Threads holes={holes} placement={placement} />
                        )}
                        {activePage === DFM_FEEDBACK_KEY &&
                            (isImOrder
                                ? dfmReports && (
                                      <DfmReportsWrapper
                                          dfmReports={dfmReports}
                                          qmrId={qmrId}
                                          quote={quote}
                                          refetchQuery={refetchQuery}
                                      />
                                  )
                                : dfmFeedback && (
                                      <ManufacturingFeedback
                                          dfmFeedbacks={dfmFeedback}
                                      />
                                  ))}
                        {shouldTrackShipmentPage && (
                            <Shipments shipmentDetails={shipmentDetail} />
                        )}
                    </PartDetailsCard>
                </Box>
            </Box>
        </Modal>
    )
}
